import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Code2, BrainCircuit, Zap, Lightbulb, FolderKanban, ShieldCheck, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { serviceIdMap } from '../data/serviceDetails';

const ServicesSection = () => {
  const { language, t } = useLanguage();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const y = useTransform(scrollYProgress, [0, 1], [100, -100]);

  const icons = [Code2, BrainCircuit, Zap, Lightbulb, FolderKanban, ShieldCheck];

  const serviceItems = t('services.items');
  const serviceIds = serviceIdMap[language];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { 
        duration: 0.8, 
        ease: [0.25, 0.46, 0.45, 0.94]
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const getServiceUrl = (index) => {
    const serviceId = serviceIds[index];
    return language === 'es' ? `/servicios/${serviceId}` : `/services/${serviceId}`;
  };

  return (
    <section 
      id="services" 
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#FAFAFA] relative overflow-hidden" 
      data-testid="services-section"
    >
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16 md:mb-24"
        >
          <motion.span 
            variants={titleVariants}
            className="label-tag text-[#E03A1C] mb-4 block"
          >
            {t('services.label')}
          </motion.span>
          <div className="overflow-hidden">
            <motion.h2 
              variants={titleVariants}
              className="text-3xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-[#0A0A0B]"
            >
              {t('services.title1')}{' '}
              <span className="text-[#E03A1C]">{t('services.title2')}</span>
            </motion.h2>
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {serviceItems.map((service, index) => {
            const IconComponent = icons[index];
            const isHighlighted = index === 0 || index === 1; // First two cards highlighted
            
            return (
              <Link
                key={index}
                to={getServiceUrl(index)}
                className="block"
              >
                <motion.div
                  variants={itemVariants}
                  whileHover={{ 
                    scale: 1.02, 
                    y: -8,
                    transition: { duration: 0.3 }
                  }}
                  className={`rounded-2xl p-8 relative overflow-hidden group cursor-pointer h-full border transition-all duration-300 ${
                    isHighlighted 
                      ? 'bg-[#FFEDE8] border-[#FFEDE8] hover:border-[#E03A1C]/30' 
                      : 'bg-white border-[#E4E4E7] hover:border-[#E03A1C]/30'
                  }`}
                  data-testid={`service-card-${index}`}
                >
                  {/* Icon */}
                  <div className="relative z-10 mb-6">
                    <motion.div 
                      className="w-14 h-14 rounded-xl bg-[#0A0A0B] flex items-center justify-center group-hover:bg-[#E03A1C] transition-colors duration-300"
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <IconComponent className="w-7 h-7 text-[#FAFAFA]" />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#0A0A0B] mb-3 flex items-center gap-2 tracking-tight">
                      {service.title}
                      <ArrowUpRight className="w-5 h-5 text-[#E03A1C] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                    <p className="text-[#52525B] leading-relaxed mb-6 text-sm md:text-base">
                      {service.description}
                    </p>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2">
                      {service.features.map((feature, idx) => (
                        <motion.span
                          key={idx}
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          transition={{ delay: (index * 0.05) + (idx * 0.1) }}
                          viewport={{ once: true }}
                          className="label-tag text-[10px] px-3 py-1.5 rounded-full bg-[#0A0A0B]/5 text-[#52525B] group-hover:bg-[#E03A1C]/10 group-hover:text-[#E03A1C] transition-colors duration-300"
                        >
                          {feature}
                        </motion.span>
                      ))}
                    </div>
                  </div>

                  {/* Hover line accent */}
                  <motion.div 
                    className="absolute bottom-0 left-0 h-1 bg-[#E03A1C]"
                    initial={{ width: 0 }}
                    whileHover={{ width: "100%" }}
                    transition={{ duration: 0.5 }}
                  />

                  {/* View more indicator */}
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="text-xs font-mono text-[#E03A1C]">
                      {language === 'es' ? 'Ver más →' : 'Learn more →'}
                    </span>
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-center mt-16"
        >
          <p className="text-[#52525B] mb-6">
            {t('services.notFound')}
          </p>
          <motion.a
            href="#contact"
            className="inline-flex items-center gap-2 text-[#E03A1C] font-semibold hover:underline group"
            whileHover={{ x: 5 }}
            data-testid="services-contact-link"
          >
            {t('services.cta')}
            <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
